import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useImpersonation } from "../../../contexts/ImpersonationContext";
import { TBankAccountFormValues } from "../../../models/Onboarding/onboarding";
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from "../../../store";
import { hideTransparentPreloaderAction, showTransparentPreloaderAction } from "../../../store/application/slice";
import { setBankAccErrorMsgAction } from "../../../store/onboarding/slice";
import { saveBankAccountThunk } from "../../../store/onboarding/thunk";
import { TCompletionStatus } from "../../../store/onboarding/types";
import { getCandidateData } from "../../../store/superadmin/thunks";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import { isSuperAdmin } from "../../../utils/roleHelper";
import CustomButton from "../../UI/Buttons/CustomButton";
import RightArrow from "../../UI/Icons/RightArrow";
import RoundedTick from "../../UI/Icons/RoundedTick";
import TextInput from "../../UI/Inputs/TextInput";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";

const BankAccount: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isImpersonating } = useImpersonation();

    const [isDisabled, setIsDisabled] = useState<boolean>(true)

    const inProgress = useSelector(
        (state) => state.onboarding.inProgress
    );
    const bankAccErrors = useSelector(
        (state) => state.onboarding.bankAccErrors
    );
    const reviewCandidateData = useSelector((state) => state.superadmin.reviewCandidateData)
    const user = useSelector((state) => state.auth.user);
    const isAdmin = isSuperAdmin(user);

    const bankAccountDetails = useSelector(
        (state) =>
        isAdmin ? state.superadmin.reviewCandidateData?.bank_account :
        state.onboarding.bankAccount
    );
    const stepCompletion: TCompletionStatus | null = useSelector(
        (state) => state.onboarding.completedstatus
    );
    const profileData = useSelector((state) => state.onboarding.personaldetails);
    const currentStage = profileData?.current_stage ? profileData?.current_stage : null;
    const reviewStatus = profileData?.verification_status;

    const shouldFieldBeDisabled = () => {
        if (isImpersonating) return false;
        if(isAdmin) return false;
        return reviewStatus === 'verified';
    };

    const checkStageSubmittedOrNot = (stageNumber: number | null) => {
        return shouldFieldBeDisabled();
    }

    /** Submit the form for bank-account-details */
    const { handleSubmit, values, errors, touched, handleChange } = useFormik({
        validationSchema: createValidationSchema("bankAccount"),
        validateOnBlur: false,
        validateOnChange: false,
        initialValues: {
            account_name: bankAccountDetails?.account_name ?? "",
            account_bsb: bankAccountDetails?.account_bsb ?? "",
            account_number: bankAccountDetails?.account_number ?? "",
        },
        onSubmit: async (values: TBankAccountFormValues) => {
            dispatch(setBankAccErrorMsgAction({}))
            const payload = { ...values };
            payload.candidate_uuid = isAdmin ? reviewCandidateData?.uuid || "" : "";
            try {
                const result = await dispatch(saveBankAccountThunk(payload));
                if(isFulfilled(result)){
                    dispatch(showTransparentPreloaderAction())
                    await dispatch(  getCandidateData({ candidate_id: reviewCandidateData?.uuid || "" })  );
                    isAdmin ? navigate("/admin/candidate-profile/next-of-kin") : navigate("/candidate/my-profile/next-of-kin")
                    dispatch(hideTransparentPreloaderAction())
                }
            } catch (error) {
                throw new Error(
                "Could not connect to services at the moment. Please try again. Thanks."
                );
            }
        },
    });

    useEffect(() => {
        if(values.account_name && values.account_bsb && values.account_number){
            setIsDisabled(!true)
        } else {
            setIsDisabled(true)
        }
    }, [values])

    const handleNext = () => {
        navigate(isAdmin ? "/admin/candidate-profile/next-of-kin" : "/candidate/my-profile/next-of-kin");
    }

    return (
        
            <div className="d-flex flex-column gap-20">

                <div className="d-flex gap-20 align-items-center justify-content-between">
                    <div className="fs-30 fw-400 text-black">Bank Account</div>
                    {
                        isAdmin && (
                            <CustomButton
                                isLoading={inProgress}
                                disabled={isDisabled}
                                contentClass="d-flex"
                                type="submit"
                                variant="primary"
                                className="btn-theme bg-teal text-black"
                                onClick={handleSubmit}
                            >
                                <RoundedTick color={isDisabled ? "#757575" : "black"}/>
                                Save Changes
                            </CustomButton>
                        )
                    }
                    
                </div>
                <div className="fs-16 fw-400 text-black">
                    Please provide your bank account details where you want to receive payments.
                </div>
                <div className="d-flex flex-column gap-20 max-w-450px">
                    <div className="flex-fill">
                        <TextInput
                            value={values.account_name}
                            field={"account_name"}
                            label={"Account Name"}
                            onChange={handleChange}
                            touched={touched.account_name! as boolean}
                            error={errors.account_name as string}
                            placeholder={" " + ""}
                            disabled={checkStageSubmittedOrNot(3)}
                        />
                        {!errors.account_name && bankAccErrors?.account_name?.map((msg, index) => (
                            <ErrorMessage className="w-100" key={`bsb-${index}`} message={msg} />
                        ))}
                    </div>
                    <div className="d-flex flex-column flex-lg-row gap-10">
                        <div className="w-180px mobile-w-100">
                            <TextInput
                                value={values.account_bsb}
                                field={"account_bsb"}
                                label={"BSB"}
                                onChange={handleChange}
                                touched={touched.account_bsb! as boolean}
                                error={errors.account_bsb as string}
                                placeholder={" " + ""}
                                disabled={checkStageSubmittedOrNot(3)}
                            />
                            {bankAccErrors?.account_bsb?.map((msg, index) => (
                                <ErrorMessage className="w-180px" key={`bsb-${index}`} message={msg} />
                            ))}
                        </div>
                        <div className="flex-fill">
                            <TextInput
                                value={values.account_number}
                                field={"account_number"}
                                label={"Account Number"}
                                onChange={handleChange}
                                touched={touched.account_number! as boolean}
                                error={errors.account_number as string}
                                placeholder={" " + ""}
                                disabled={checkStageSubmittedOrNot(3)}
                            />
                            {bankAccErrors?.account_number?.map((msg, index) => (
                                <ErrorMessage className="flex-fill" key={`acc-${index}`} message={msg} />
                            ))}
                        </div>
                    </div>
                    <div className="h-10px"></div>
                    { (stepCompletion?.bankAccount === 1 && checkStageSubmittedOrNot(3)) ? 
                        <div className="d-flex">
                            <CustomButton
                                type="submit"
                                variant="primary"
                                onClick={handleSubmit}
                                className="btn-theme bg-teal text-black"
                            >
                                Next
                                <RightArrow color={"#000000"} />
                            </CustomButton>
                        </div> : 
                        <div className="d-flex flex-column flex-lg-row gap-20">
                            {
                                !isAdmin && (
                                    <CustomButton
                                        isLoading={inProgress}
                                        disabled={isDisabled}
                                        contentClass="d-flex"
                                        type="submit"
                                        variant="primary"
                                        className="btn-theme bg-teal text-black"
                                        onClick={handleSubmit}
                                    >
                                        Save and Continue
                                        <RightArrow color={(isDisabled || inProgress) ? "#aaaaaa" : "#000000"} />
                                    </CustomButton>
                                )
                            }
                            
                        </div>
                    }
                </div>
            </div>
      
    );
};

export default BankAccount;
